import { QueryClient } from "@tanstack/react-query";
import { BaseLogo, ImageRef, ProductRef } from "../../core/models/entities/BaseLogo";
import { PagedResponse } from "../models/responses/pagedResponse";
import { actAsCacheKey } from "../../contexts/actAsStore";
import { enviroment } from "../../enviroment";

// Kept out of LogoRepository on purpose: that module imports types from
// useLogoActionsHandler, and the actions handler needs these helpers - a
// separate leaf module avoids an import cycle.

// Detail cache key under the CURRENT act-as scope - the exact shape
// LogoRepository.useLogo reads (["logos", actAs, id]).
export function logoDetailKey(logoId: BaseLogo["id"]): (string | number)[] {
  return ["logos", actAsCacheKey(), logoId];
}

// A detail entry: ["logos", actAs, id] (LogoRepository.useLogo). Every other
// "logos" key is length 4 (list: ["logos", actAs, typeFilter, qs]; infinite:
// ["logos", "inf", actAs, qs]; design picker: ["logos", customerId, "inf",
// qs]) or the bare ["logos"] prefix, so length-3-with-two-numbers uniquely
// identifies a detail entry. ONE definition, shared by every predicate that
// must recognize (or exclude) detail entries - the old hand-rolled
// `length === 2 && typeof key[1] === "number"` checks silently stopped
// matching when the act-as segment entered the key. Lives in this leaf
// module (re-exported by LogoRepository for its historical importers) so the
// cache patch below can use it without an import cycle.
export function isLogoDetailQueryKey(key: readonly unknown[]): boolean {
  return (
    key[0] === "logos" &&
    key.length === 3 &&
    typeof key[1] === "number" &&
    typeof key[2] === "number"
  );
}

/**
 * The act-as segment of a ["logos", ...] query key - the scope whose rows
 * the entry holds: ["logos", actAs, id] (detail), ["logos", actAs,
 * typeFilter, qs] (paged list) and ["logos", "inf", actAs, qs] (infinite
 * list). Undefined for every other form - the design picker's ["logos",
 * customerId, "inf", qs] carries no act-as segment, so which organization's
 * rows it holds cannot be told from the key. Re-exported by
 * utils/functions/storageFreshness for its historical importers.
 */
export function logoQueryKeyScope(key: readonly unknown[]): number | undefined {
  if (key[0] !== "logos") return undefined;
  if (isLogoDetailQueryKey(key)) return key[1] as number;
  if (key.length !== 4) return undefined;
  if (key[1] === "inf") {
    return typeof key[2] === "number" ? key[2] : undefined;
  }
  if (typeof key[1] === "number" && key[2] !== "inf") return key[1];
  return undefined;
}

// Explicit "no genuine high-res yet" mark on a cached detail entry (flag
// REACT_APP_LOGO_HEAL_PER_MUTATION). The copy response carries the 180px
// processed blob AS highResUrl (a data-URI that differs from lowResUrl), so
// the heal's equality rule never sees a clone as pending; the clone flows
// stamp the seeded entry instead and isHighResPending honours the field.
// No-op when the entry does not exist (never creates a fabricated entry).
export function markDetailHighResPending(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
): void {
  const key = logoDetailKey(logoId);
  const current = queryClient.getQueryData<BaseLogo>(key);
  if (!current || !current.displayImage) return;
  if (current.displayImage.highResPending === true) return;
  queryClient.setQueryData<BaseLogo>(key, {
    ...current,
    displayImage: { ...current.displayImage, highResPending: true },
  });
}

// "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED): the detail
// entry of a worker-owned upload is seeded with the user's own file as its
// canvas bitmap - an object URL (seedPendingUpload). When that URL is revoked
// (Cancel, another logo opened, eviction, the row removed) the entry must
// not keep it: refetchOnMount is off globally, so re-opening the still
// pending logo would hand the editor and the popup a dead blob: URL and a
// broken image (skeptic round 2). The entry is put back into the shape the
// API's own answer has for a pending upload - the 180 px thumbnail as both
// URLs (the mapper's "no high-res" form, painted at natural size by the
// editor) or no bitmap at all (vector / no thumbnail = the file card), and
// highResPending true. `key` is the entry the seed was written to (captured
// at seed time, so an act-as switch in between cannot miss it). No-op when
// the entry no longer shows the URL (a genuine high-res landed first, or
// the entry is gone). Never called with the flag off (nothing registers).
export function dropRevokedUploadUrl(
  queryClient: QueryClient,
  key: readonly unknown[],
  url: string | null | undefined,
): void {
  if (!url) return;
  const current = queryClient.getQueryData<BaseLogo>(key as unknown[]);
  const image = current?.displayImage;
  if (!image || image.highResUrl !== url) return;
  const lowResUrl = image.lowResUrl === url ? undefined : image.lowResUrl;
  queryClient.setQueryData<BaseLogo>(key as unknown[], {
    ...current,
    displayImage: {
      ...image,
      lowResUrl,
      highResUrl: lowResUrl,
      highResPending: true,
    } as ImageRef,
  });
}

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION: drop a heal poll (GetLogo) that is
// still in flight for this logo right before a mutation response is seeded.
// Without this a poll started BEFORE the mutation can land AFTER its
// response, overwrite the seeded entry with the pre-mutation render and -
// because that render may carry a genuine high-res - stop the heal, so the
// synced canvas would show the old colours/size until F5. react-query
// ignores the result of a cancelled fetch and reverts the entry to its
// pre-fetch state, which the seed then replaces. No-op when nothing is in
// flight; a no-op with the flag off (the canvas is not synced then).
// Flag REACT_APP_PRODUCT_PENDING_ENABLED needs the same guard on its own:
// its 3/8/15 s re-read of this entry (useProductPendingPoll) started before
// a mutation would otherwise land after the response with the PRE-mutation
// product (productPending=false, old prices) - badge gone, old price shown
// until the next poll. Flag REACT_APP_CLONE_INSTANT_ENABLED likewise: its
// "Preparing logo…" re-read (useEditorUploadPendingPoll) must not land a
// pre-mutation detail over the seeded response when it runs alone (heal and
// product flags off). All three flags off = the no-op as before.
export function cancelInFlightDetailFetch(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
): void {
  if (
    !enviroment.logoHealPerMutationEnabled &&
    !enviroment.productPendingEnabled &&
    !enviroment.cloneInstantEnabled
  ) {
    return;
  }
  void queryClient.cancelQueries({
    queryKey: logoDetailKey(logoId),
    exact: true,
  });
}

// Flag REACT_APP_LOGO_POPUP_FAST: seed the detail entry with the LIST row the
// popup was opened from, so useLogo has data on its very first render and the
// popup paints the row's image, prices, colours and stock at once. Only when
// the entry is EMPTY or still a STUB from an earlier open - a cached GetLogo
// (earlier open, deep-link seed, mutation seed) is always better than a row
// and is never overwritten. The stub is stamped detailFromList (see BaseLogo)
// and written with updatedAt 0, i.e. stale from birth; the caller fires the
// ONE background GetLogo that replaces it (refetchOnMount is off globally, so
// nothing else would). The zero stamp also means "never loaded" to
// react-query, so a stub left behind by a popup closed before its GetLogo
// landed is loaded on the next mount anyway. A stub found here is seeded
// AGAIN rather than kept: that resets it to updatedAt 0 and status success
// whatever happened to it meanwhile (a failed GetLogo left it in error; a
// writer that stamped it fresh would otherwise make the mount skip the load)
// - so it can never stick. Returns whether a seed was written. Flag off =
// no-op, nothing touched.
export function seedLogoDetailFromList(
  queryClient: QueryClient,
  logo: BaseLogo,
): boolean {
  if (!enviroment.logoPopupFastEnabled) return false;
  const key = logoDetailKey(logo.id);
  const current = queryClient.getQueryData<BaseLogo>(key);
  if (current !== undefined && !isLogoDetailFromList(current)) return false;
  queryClient.setQueryData<BaseLogo>(
    key,
    { ...logo, detailFromList: true },
    { updatedAt: 0 },
  );
  return true;
}

// True while the detail entry is still the list-row stub above (the GetLogo
// response never carries the mark). Consumers that must judge the REAL
// detail (heal poll, "prices on their way" poll, View designs) pass
// undefined instead of a stub.
export function isLogoDetailFromList(logo: BaseLogo | undefined): boolean {
  return logo?.detailFromList === true;
}

// ---------------------------------------------------------------------------
// "Prices on their way" / "Preparing logo…" in the LISTS (flags
// REACT_APP_PRODUCT_PENDING_ENABLED / REACT_APP_CLONE_INSTANT_ENABLED).
//
// The list entries (["logos", actAs, typeFilter, qs], staleTime 60 s,
// refetchOnMount off) are only ever rewritten by a query change, a mutation
// invalidation or the return-to-list refetch - and that refetch races the
// API worker: a resize stamps productPending, the user goes back to /logo,
// the list is refetched at once (worker not done -> row pending) and nothing
// afterwards writes a GetLogo answer back into the row, so the badge stays
// until F5 (prod 675889, 11/9). Every pending re-read (popup poll, editor
// heal/poll, cart sync, the list poll) lands in the DETAIL entry; this
// helper copies the landed pending state (and the worker's product/prices)
// from that detail into the row with the same id in every list entry of the
// same act-as scope. 0 calls: pure cache write.

function isPagedLogos(data: unknown): data is PagedResponse<BaseLogo> {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as PagedResponse<BaseLogo>).results)
  );
}

function isInfiniteLogos(
  data: unknown,
): data is { pages: PagedResponse<BaseLogo>[] } {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

function sameProduct(a: ProductRef | undefined, b: ProductRef | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.sku === b.sku &&
    JSON.stringify(a.prices ?? null) === JSON.stringify(b.prices ?? null) &&
    JSON.stringify(a.additionalFees ?? null) ===
      JSON.stringify(b.additionalFees ?? null)
  );
}

// A row is only touched while it - or the detail - is pending under an
// ENABLED variant (the flag-gated predicates in productPending.ts are the
// same rule the badge renders on), and only the fields of the enabled
// variants plus the product are written. Rows that are not pending on either
// side keep their identity even when the detail carries newer prices: this
// patch is about the pending state, not a general "detail wins" sync. A row
// with nothing to change is returned AS IS (reference check = no dispatch).
function patchRowFromDetail(row: BaseLogo, detail: BaseLogo): BaseLogo {
  if (!row || row.id !== detail.id) return row;
  const productEnabled = enviroment.productPendingEnabled;
  const uploadEnabled = enviroment.cloneInstantEnabled;
  const rowPending =
    (productEnabled && row.productPending === true) ||
    (uploadEnabled && row.uploadPending === true);
  const detailPending =
    (productEnabled && detail.productPending === true) ||
    (uploadEnabled && detail.uploadPending === true);
  if (!rowPending && !detailPending) return row;

  const productSame =
    !productEnabled ||
    ((row.productPending === true) === (detail.productPending === true) &&
      (row.productPendingSinceUtc ?? null) ===
        (detail.productPendingSinceUtc ?? null));
  const uploadSame =
    !uploadEnabled ||
    ((row.uploadPending === true) === (detail.uploadPending === true) &&
      (row.uploadPendingSinceUtc ?? null) ===
        (detail.uploadPendingSinceUtc ?? null));
  // "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED, on top of
  // the clone flag): the kind and the worker's rejection verdict travel with
  // the stamp - a rejected upload keeps its stamp by design, so without
  // these the list row would never learn it ("Preparing logo…" instead of
  // "Upload failed", and the list re-read would keep spending its budget
  // on a row nothing will ever land on). Flag off = neither compared nor
  // written = byte-identical rows.
  const uploadKindEnabled = uploadEnabled && enviroment.uploadPendingEnabled;
  const uploadKindSame =
    !uploadKindEnabled ||
    ((row.uploadPendingKind ?? null) === (detail.uploadPendingKind ?? null) &&
      (row.uploadFailedReason ?? null) === (detail.uploadFailedReason ?? null) &&
      (row.uploadFailedMessage ?? null) ===
        (detail.uploadFailedMessage ?? null) &&
      (row.uploadFailedUtc ?? null) === (detail.uploadFailedUtc ?? null));
  if (
    productSame &&
    uploadSame &&
    uploadKindSame &&
    sameProduct(row.product, detail.product)
  ) {
    return row;
  }
  const next: BaseLogo = { ...row, product: detail.product };
  if (productEnabled) {
    next.productPending = detail.productPending;
    next.productPendingSinceUtc = detail.productPendingSinceUtc;
  }
  if (uploadEnabled) {
    next.uploadPending = detail.uploadPending;
    next.uploadPendingSinceUtc = detail.uploadPendingSinceUtc;
  }
  if (uploadKindEnabled) {
    next.uploadPendingKind = detail.uploadPendingKind ?? null;
    next.uploadFailedReason = detail.uploadFailedReason ?? null;
    next.uploadFailedMessage = detail.uploadFailedMessage ?? null;
    next.uploadFailedUtc = detail.uploadFailedUtc ?? null;
  }
  return next;
}

type RowPatch = (row: BaseLogo) => BaseLogo;

function mapResults(
  results: BaseLogo[] | undefined,
  patchRow: RowPatch,
): BaseLogo[] | undefined {
  if (!Array.isArray(results)) return results;
  let changed = false;
  const next = results.map((row) => {
    const patched = patchRow(row);
    if (patched !== row) changed = true;
    return patched;
  });
  return changed ? next : results;
}

// A cached list entry - a paged list ({results}) or an infinite list
// ({pages: [{results}]}) - with `patchRow` applied to every row. Any other
// shape, or no row changed, comes back as the very same reference (no
// dispatch). Shared by the detail patch below and the editor's pending
// mark (markLogoRowsProductPending).
function mapCachedLogoRowsData(data: unknown, patchRow: RowPatch): unknown {
  if (!data) return data;
  if (isPagedLogos(data)) {
    const results = mapResults(data.results, patchRow);
    return results === data.results ? data : { ...data, results };
  }
  if (isInfiniteLogos(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      if (!isPagedLogos(page)) return page;
      const results = mapResults(page.results, patchRow);
      if (results === page.results) return page;
      changed = true;
      return { ...page, results };
    });
    return changed ? { ...data, pages } : data;
  }
  return data;
}

/**
 * A cached list entry with the detail's pending state applied to the row of
 * the same id: a paged list ({results}) or an infinite list ({pages:
 * [{results}]}). Any other shape, or nothing to change, comes back as the
 * very same reference. Exported for tests.
 */
export function patchCachedLogoRowsData(data: unknown, detail: BaseLogo): unknown {
  return mapCachedLogoRowsData(data, (row) => patchRowFromDetail(row, detail));
}

/**
 * Copies the pending state a landed GetLogo (`detail`) carries into the row
 * with the same id in every LIST entry under ["logos"] that belongs to the
 * current act-as scope (same rule as patchStorageInQueryCache): the paged
 * lists (logo list screen) and the infinite lists (design editor picker).
 * Written per row: productPending / productPendingSinceUtc (product flag),
 * uploadPending / uploadPendingSinceUtc (clone flag), uploadPendingKind +
 * uploadFailedReason / Message / Utc (upload-pending flag on top of the
 * clone flag: the worker's rejection verdict) and `product` (the worker's
 * sku/prices) - never the whole detail (its high-res data-URIs must not
 * grow the list), never a row that is pending on neither side.
 *
 * Never written: detail entries (the caller's own entry included), another
 * act-as scope's lists (a previous vendor's rows, still cached, must not get
 * this scope's prices), the design picker's unscoped entry, and a list that
 * is NEWER than the detail entry (dataUpdatedAt): a cached GetLogo from
 * minutes ago cannot clear a badge the list was told about since. The
 * popup's list-row stub (isLogoDetailFromList) is not a detail and patches
 * nothing. Entries that would not change are not written at all - no
 * dispatch, no re-render. Returns the number of entries written. Both flags
 * off = no-op, 0 (byte-identical cache).
 *
 * `options.preserveFreshness` (the cart price list sync's sub-flag,
 * useCartSync): the write keeps the page's own dataUpdatedAt and a pending
 * invalidation (the same guarantee as cartPriceListSync's price patch)
 * instead of stamping the page `now`. A page stamped `now` by this LOCAL
 * patch would count as "newer than the landing" for the price patch of a
 * logo whose GetLogo landed in the window between the pending logo's
 * commit and its effect (React runs passive effects after paint), and that
 * logo's row would keep its old price until its next landing (skeptic
 * round 3). With the option no local patch is ever newer than a server
 * landing. Default false = the historical plain setQueryData, byte-identical
 * for the pending flags on their own.
 */
export function patchLogoRowsFromDetail(
  queryClient: QueryClient,
  detail: BaseLogo | null | undefined,
  options?: { preserveFreshness?: boolean },
): number {
  if (!enviroment.productPendingEnabled && !enviroment.cloneInstantEnabled) {
    return 0;
  }
  if (!detail || typeof detail.id !== "number") return 0;
  if (isLogoDetailFromList(detail)) return 0;
  const scope = actAsCacheKey();
  const detailUpdatedAt =
    queryClient.getQueryState(logoDetailKey(detail.id))?.dataUpdatedAt ?? 0;
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["logos"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (isLogoDetailQueryKey(key)) return;
      if (logoQueryKeyScope(key) !== scope) return;
      if (detailUpdatedAt > 0 && query.state.dataUpdatedAt > detailUpdatedAt) {
        return;
      }
      const current = query.state.data;
      if (current == null) return;
      const next = patchCachedLogoRowsData(current, detail);
      if (next === current) return;
      if (options?.preserveFreshness) {
        // Inlined rather than designCachePatch.writePreservingFreshness:
        // designCachePatch -> storageFreshness -> this module would be a
        // cycle. Same two steps: keep the stamp, re-mark a pending
        // invalidation (setQueryData clears it) without a refetch.
        const wasInvalidated = query.state.isInvalidated;
        queryClient.setQueryData(key, next, {
          updatedAt: query.state.dataUpdatedAt || undefined,
        });
        if (wasInvalidated) {
          void queryClient.invalidateQueries({
            queryKey: key as unknown[],
            exact: true,
            refetchType: "none",
          });
        }
      } else {
        queryClient.setQueryData(key, next);
      }
      written += 1;
    });
  return written;
}

// ---------------------------------------------------------------------------
// "Prices on their way" mark from the EDITOR's "Refresh prices now" (flag
// REACT_APP_LOGO_PRICE_REFRESH_NOW_EDITOR_ENABLED; useLogoPriceRefreshNow
// with cacheWrite "rows").

function markRowProductPending(
  row: BaseLogo,
  logoId: BaseLogo["id"],
  sinceUtc: string,
): BaseLogo {
  if (!row || row.id !== logoId) return row;
  if (
    row.productPending === true &&
    (row.productPendingSinceUtc ?? null) === sinceUtc
  ) {
    return row;
  }
  return { ...row, productPending: true, productPendingSinceUtc: sinceUtc };
}

/**
 * Marks the row of `logoId` "Prices on their way" (productPending=true + the
 * stamp the API's product-refresh answer carries) in every LIST entry under
 * ["logos"] of the current act-as scope - the paged lists (logo list screen)
 * and the infinite lists (design editor picker) - and NOTHING else: the row
 * keeps its product/prices (the list may hold a newer product than any
 * detail the caller has), every other row keeps its identity, and detail
 * entries, other act-as scopes and the unscoped design picker are never
 * written. The DETAIL entry is deliberately left alone - the editor's heal
 * subscription (useEditorLogoHeal) hands every change of it to the canvas
 * sync (patchLogoFromDetail: keepSharp, the held upload's poll budget) and
 * re-arms the hi-res heal timer, none of which a pending mark may touch.
 * The mark is cleared by the polls that land the worker's answer in the
 * detail entry and copy it into the rows (the editor's own heal / pending
 * polls through useEditorLogoHeal's row patch, or the list poll -
 * useLogoListPendingPoll, a fresh budget for a NEW pending row - once the
 * admin is back on the list).
 *
 * The write stamps each written page `now` (a plain setQueryData, the same
 * write the popup's detail path makes through patchLogoRowsFromDetail): the
 * API has just written the stamp, so the mark IS the newest truth, and the
 * page's fresh stamp is what protects it. Every reader that copies a detail
 * into the rows (patchLogoRowsFromDetail: the popup wrapper on open, the
 * cart sync, useEditorLogoHeal on a re-mount) skips a page that is NEWER
 * than the detail entry it reads - and in the editor that entry is always a
 * real GetLogo that landed BEFORE the click (the editor mounts on it and
 * nothing re-reads it on its own: refetchOnMount is off globally), saying
 * productPending=false with the old product. Kept at the page's old stamp
 * (skeptic round 1) that guard did not fire: opening the popup on the row
 * cleared the mark and showed the old prices with the button open, and the
 * list poll - which excludes the popup's row - never got to land the
 * answer. A landing AFTER the click (the list poll's GetLogo, the editor's
 * heal / pending polls) is newer than the mark and clears it as before. A
 * pending invalidation on the page is re-marked (setQueryData clears it) so
 * the click changes nothing about when the list refetches; the list's
 * refetch on return is the sessionStorage flag (consumeLogoListStaleFlag),
 * not the stamp, and refetchOnMount / refetchOnWindowFocus are off - the
 * click causes no extra list refetch. A row already carrying that exact
 * mark is not written. Returns the number of entries written. Flag
 * REACT_APP_PRODUCT_PENDING_ENABLED off = no-op, 0 (nothing would read the
 * fields; the cache stays byte-identical).
 */
export function markLogoRowsProductPending(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
  sinceUtc: string,
): number {
  if (!enviroment.productPendingEnabled) return 0;
  if (typeof logoId !== "number" || !(logoId > 0)) return 0;
  const scope = actAsCacheKey();
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["logos"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (isLogoDetailQueryKey(key)) return;
      if (logoQueryKeyScope(key) !== scope) return;
      const current = query.state.data;
      if (current == null) return;
      const next = mapCachedLogoRowsData(current, (row) =>
        markRowProductPending(row, logoId, sinceUtc),
      );
      if (next === current) return;
      const wasInvalidated = query.state.isInvalidated;
      queryClient.setQueryData(key, next);
      if (wasInvalidated) {
        void queryClient.invalidateQueries({
          queryKey: key as unknown[],
          exact: true,
          refetchType: "none",
        });
      }
      written += 1;
    });
  return written;
}
